import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'

import type { Page, Post, Category } from '@/payload-types'

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts' | 'categories'
    value: Page | Post | Category | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
  // Heuristic/controls for rendering label with/without children
  renderLabelStrategy?: 'auto' | 'always' | 'never'
  labelPlacement?: 'before' | 'after'
  labelClassName?: string
  // Allow passing through common aria/data attributes
  [key: string]: any
}

/**
 * Generate href for CMS links with breadcrumb support
 * Following PayloadCMS website patterns
 */
const generateHref = (props: CMSLinkType): string => {
  const { type, reference, url } = props

  if ((type === 'custom' || type === undefined) && url) {
    return url
  }

  if (type === 'reference' && reference?.value && typeof reference.value !== 'string') {
    const { relationTo, value } = reference

    if (relationTo === 'pages') {
      const page = value as Page

      // First try to use the URL field if it exists
      if ((page as any)?.url) {
        return (page as any).url as string
      }

      // Fallback to breadcrumbs
      const breadcrumbs = (page as any)?.breadcrumbs
      const hasBreadcrumbs = breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0

      if (hasBreadcrumbs) {
        return breadcrumbs[breadcrumbs.length - 1]?.url as string
      }

      // Final fallback to simple slug
      return page.slug === 'home' ? '/' : `/${page.slug}`
    }

    if (relationTo === 'posts') {
      const post = value as Post

      // First try to use the URL field if it exists (category-based URL)
      if ((post as any)?.url) {
        return (post as any).url as string
      }

      // Fallback to simple blog structure
      return `/blog/${post.slug}`
    }

    if (relationTo === 'categories') {
      // Categories don't have their own routing - return empty or redirect to blog
      return '/blog'
    }
    return `/${relationTo}/${(value as any).slug}`
  }

  return ''
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    renderLabelStrategy = 'auto',
    labelPlacement = 'before',
    labelClassName,
    ...rest
  } = props

  const href = generateHref(props)

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  // Helper: detect if children tree has any visible text
  const hasTextInChildren = (node: React.ReactNode): boolean => {
    if (node === null || node === undefined || typeof node === 'boolean') return false
    if (typeof node === 'string') return node.trim().length > 0
    if (typeof node === 'number') return true
    if (Array.isArray(node)) return node.some((n) => hasTextInChildren(n))
    if (React.isValidElement(node)) return hasTextInChildren((node.props as any)?.children)
    return false
  }

  const hasChildren = React.Children.count(children) > 0
  const childrenHasText = hasChildren ? hasTextInChildren(children) : false

  // Decide what to render based on strategy
  let renderChildren = hasChildren
  let renderLabel = false

  if (renderLabelStrategy === 'never') {
    renderLabel = !hasChildren && !!label
  } else if (renderLabelStrategy === 'always') {
    renderLabel = !!label
  } else {
    // auto
    if (hasChildren) {
      if (childrenHasText) {
        renderLabel = false // children already contain visible text
        if (process.env.NODE_ENV !== 'production' && label) {
          // eslint-disable-next-line no-console
          console.warn('[CMSLink] children contain text; label prop ignored in auto mode', {
            href,
            label,
          })
        }
      } else {
        // Icon-only children: render label + children if label provided
        renderLabel = !!label
      }
    } else {
      renderLabel = !!label
    }
  }

  // Accessible name is provided by visible text when renderLabel is true or when childrenHasText
  const ariaProps = {} as Record<string, any>

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link className={cn(className)} href={href} {...newTabProps} {...ariaProps} {...rest}>
        {renderLabel && labelPlacement === 'before' ? (
          <span className={cn('cmslink-label', labelClassName)}>{label}</span>
        ) : null}
        {renderChildren ? children : null}
        {renderLabel && labelPlacement === 'after' ? (
          <span className={cn('cmslink-label', labelClassName)}>{label}</span>
        ) : null}
      </Link>
    )
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <Link className={cn(className)} href={href} {...newTabProps} {...ariaProps} {...rest}>
        {renderLabel && labelPlacement === 'before' ? (
          <span className={cn('cmslink-label', labelClassName)}>{label}</span>
        ) : null}
        {renderChildren ? children : null}
        {renderLabel && labelPlacement === 'after' ? (
          <span className={cn('cmslink-label', labelClassName)}>{label}</span>
        ) : null}
      </Link>
    </Button>
  )
}
